/**
 * Le mie provvigioni — vista read-only per l'agente loggato.
 * Filtri: ricerca, periodo (mese), stato + raggruppamento per cliente (default).
 */
import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Wallet, Search } from "lucide-react";
import { Card } from "./ui/card";
import { Input } from "./ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import CommissionRowsTable, { CommissionGroups } from "./CommissionRowsTable";
import { API } from "../utils/format";
import { formatMoney } from "../utils/currency";

const fmtDate = (iso) => (iso ? new Date(iso + (iso.length === 10 ? "T00:00:00" : "")).toLocaleDateString("it-IT") : "—");
const monthLabel = (ym) => {
  const [y, m] = ym.split("-");
  return `${m}/${y}`;
};

const KPI = ({ label, value, accent = "text-stone-900", testId }) => (
  <Card className="bg-white border-stone-200 p-3" data-testid={testId}>
    <p className="text-[10px] text-stone-500 uppercase tracking-wide">{label}</p>
    <p className={`text-lg font-bold ${accent}`}>{value}</p>
  </Card>
);

export default function AgentMyCommissions({ company }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [period, setPeriod] = useState("all");
  const [view, setView] = useState("customer");

  useEffect(() => {
    axios.get(`${API}/agent/my-commissions`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e.response?.data?.detail || "Errore caricamento provvigioni"));
  }, []);

  const months = useMemo(() => {
    const s = new Set((data?.rows || []).map((r) => (r.doc_date || "").slice(0, 7)).filter(Boolean));
    return [...s].sort().reverse();
  }, [data]);

  const rows = useMemo(() => (data?.rows || []).filter((r) => {
    if (filter === "residual" && r.commission_residual <= 0.005) return false;
    if (filter === "paid" && r.commission_residual > 0.005) return false;
    if (period !== "all" && !(r.doc_date || "").startsWith(period)) return false;
    if (search) {
      const q = search.toLowerCase();
      if (!(r.customer_name || "").toLowerCase().includes(q) && !(r.doc_number || "").toLowerCase().includes(q) && !(r.order_number || "").toLowerCase().includes(q)) return false;
    }
    return true;
  }), [data, filter, period, search]);

  if (error) return <p className="p-6 text-sm text-red-700" data-testid="myc-error">{error}</p>;
  if (!data) return <div className="p-8 text-center"><div className="spinner w-6 h-6 mx-auto" /></div>;

  const t = data.totals;

  return (
    <div className="space-y-4" data-testid="my-commissions-page">
      <div>
        <h2 className="text-2xl font-semibold text-stone-900 flex items-center gap-2"><Wallet className="w-6 h-6 text-[#8A1C2A]" /> Le mie provvigioni</h2>
        <p className="text-stone-500 text-sm">Le provvigioni maturano in proporzione a quanto i tuoi clienti hanno pagato</p>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <KPI label="Fatturato complessivo" value={formatMoney(t.invoiced, company)} testId="myc-kpi-invoiced" />
        <KPI label="Provvigioni totali" value={formatMoney(t.due, company)} testId="myc-kpi-due" />
        <KPI label="Maturate" value={formatMoney(t.matured, company)} accent="text-blue-700" testId="myc-kpi-matured" />
        <KPI label="Pagate" value={formatMoney(t.paid, company)} accent="text-green-700" testId="myc-kpi-paid" />
        <KPI label="Residue" value={formatMoney(t.residual, company)} accent="text-[#8A1C2A]" testId="myc-kpi-residual" />
      </div>
      <Card className="p-0 overflow-hidden bg-white border-stone-200">
        <div className="p-3 border-b border-stone-100 flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-stone-400" />
            <Input placeholder="Cerca cliente o documento..." value={search} onChange={(e) => setSearch(e.target.value)} className="h-8 pl-8 w-56" data-testid="myc-search" />
          </div>
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-36 h-8 text-xs" data-testid="myc-period"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti i periodi</SelectItem>
              {months.map((m) => <SelectItem key={m} value={m}>{monthLabel(m)}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-40 h-8 text-xs" data-testid="myc-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti i documenti</SelectItem>
              <SelectItem value="residual">Con residuo</SelectItem>
              <SelectItem value="paid">Saldate</SelectItem>
            </SelectContent>
          </Select>
          <div className="flex rounded-md border border-stone-200 overflow-hidden text-xs">
            <button className={`px-3 py-1.5 ${view === "customer" ? "bg-[#8A1C2A] text-white font-semibold" : "bg-white text-stone-600"}`} onClick={() => setView("customer")} data-testid="myc-view-customer">Per cliente</button>
            <button className={`px-3 py-1.5 ${view === "list" ? "bg-[#8A1C2A] text-white font-semibold" : "bg-white text-stone-600"}`} onClick={() => setView("list")} data-testid="myc-view-list">Elenco</button>
          </div>
          <span className="ml-auto text-xs text-stone-500">{rows.length} documenti</span>
        </div>
        {view === "customer"
          ? <CommissionGroups rows={rows} company={company} />
          : <CommissionRowsTable rows={rows} company={company} />}
      </Card>
      <Card className="p-0 overflow-hidden bg-white border-stone-200">
        <div className="p-3 border-b border-stone-100 font-semibold text-sm text-stone-800">Pagamenti ricevuti</div>
        {(data.payments || []).length === 0 ? (
          <p className="p-4 text-sm text-stone-500 italic">Nessun pagamento ricevuto</p>
        ) : (
          <div className="divide-y divide-stone-100">
            {data.payments.map((p) => (
              <div key={p.id} className="p-3 text-sm" data-testid={`myc-payment-${p.id}`}>
                <p className="font-semibold text-stone-900">{formatMoney(p.amount, company)} <span className="font-normal text-stone-500">— {fmtDate(p.payment_date)}</span></p>
                <p className="text-xs text-stone-500">{p.allocations?.length || 0} documenti{p.note ? ` · ${p.note}` : ""}</p>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
